/**
 * Unified logger facade that centralizes all logging, tracking, and messaging concerns
 * while maintaining separation of responsibilities between modules
 */

import { getInitialCrashReportingSetting } from "components/app/app-helper";
import config from "config/app.config";
import {
    trackEvent as insightsTrackEvent,
    trackPageView as insightsPageView,
    InsightProps,
} from "logger/insights";
import { State } from "store/store-types";
import { sendTrackingEventToParent, TrackingEventType } from "logger/event-utils";

// Enable verbose logging in development
const verbose = false;

export function log(...args: unknown[]): void {
    console.log(...args);
}

/**
 * These are not used at the moment in the project.
 */
export function debug(...args: unknown[]): void {
    console.debug(...args);
}

export function info(...args: unknown[]): void {
    console.info(...args);
}

/**
 * Used for logging a warning. Something is wrong but we can recover.
 */
export function warn(...args: unknown[]): void {
    if (!getInitialCrashReportingSetting()) {
        console.warn(...args);
    }
}

/**
 * Used for logging an error that is not recoverable.
 * Sends telemetry data to AppInsights in production.
 */
export function error(...args: unknown[]): void {
    console.error(...args);

    // For prod: track the error in Insights
    if (!config.isDev) {
        const errorObj = args.find((arg) => arg instanceof Error);

        let properties: InsightProps = {};
        args.forEach((arg) => {
            if (arg && typeof arg === "object" && !(arg instanceof Error)) {
                properties = { ...properties, ...arg };
            }
        });

        // Create a string message from non-object arguments
        const message = args.filter((arg) => !(typeof arg === "object" && arg !== null)).join(" ");

        // Track the exception in insights
        trackError(message, errorObj, properties);
    }
}

/**
 * Internal helper for tracking errors in Application Insights
 */
function trackError(message: string, error?: Error, properties?: InsightProps): void {
    if (config.isDev) return;

    // We delegate the actual implementation to insights.ts in the future
    // For now, we just send a trackEvent since insights.ts doesn't have error tracking yet
    insightsTrackEvent("Error", {
        message,
        errorName: error?.name ?? "",
        errorMessage: error?.message ?? "",
        stack: error?.stack ?? "",
        ...properties,
    });
}

/**
 * Track a custom event in Application Insights
 */
export function trackEvent(name: string, properties?: InsightProps): void {
    // Debug logging in development
    if (verbose || config.isDev) {
        console.log(`[TRACK EVENT] ${name}`, properties);
    }

    insightsTrackEvent(name, properties);
}

/**
 * Track a page view in Application Insights
 */
export function trackPageView(name: string, pathname: string): void {
    // Debug logging in development
    if (verbose || config.isDev) {
        console.log(`[PAGE VIEW] ${name} - ${pathname}`);
    }

    insightsPageView(name, pathname);
}

/**
 * Send tracking event to parent window when running in an iframe
 */
export function sendToParent(
    state: State,
    type: TrackingEventType = TrackingEventType.userInput
): void {
    if (verbose || config.isDev) {
        console.log(`[Parent Event] ${type} - ${JSON.stringify(state)}`);
    }
    sendTrackingEventToParent(state, type);
}
